import * as React from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api, qk } from '@client/lib/api';
import { useAdminRooms, type AdminRoom } from '@client/lib/admin-api';
import { PageHeader, RoomStatusBadge } from '@client/components/admin/shared';
import { Button } from '@client/components/ui/button';
import { Dialog } from '@client/components/ui/overlay';
import { Field, Input } from '@client/components/ui/input';
import { Spinner } from '@client/components/ui/feedback';
import { Table, Td, Th, Tr } from '@client/components/ui/table';
import { fmtRelative } from '@client/lib/format';

export const Route = createFileRoute('/admin/rooms/')({ component: RoomsPage });

function RoomsPage() {
  const rooms = useAdminRooms();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const create = useMutation({
    mutationFn: () => api<{ room: AdminRoom }>('/api/admin/rooms', { body: { name } }),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: qk.admin('rooms') }); setOpen(false); navigate({ to: '/admin/rooms/$id', params: { id: res.room.id } }); },
  });
  return (
    <>
      <PageHeader title="Rooms" eyebrow="Investor Room" actions={<Button onClick={() => setOpen(true)}><Plus />New room</Button>} />
      <div className="px-6 py-6 md:px-8">
        {rooms.isPending ? <Spinner /> : (
          <div className="rounded-card border border-border bg-card">
            <Table>
              <thead><tr><Th>Room</Th><Th>Status</Th><Th align="right">Published items</Th><Th align="right">Active grants</Th><Th>Last activity</Th><Th>Updated</Th></tr></thead>
              <tbody>
                {rooms.data?.rooms.map((r) => (
                  <Tr key={r.id}>
                    <Td><Link to="/admin/rooms/$id" params={{ id: r.id }} className="font-[520]">{r.name}</Link><span className="ml-2 text-muted">/{r.slug}</span></Td>
                    <Td><RoomStatusBadge status={r.status} /></Td>
                    <Td align="right">{r.published_items}</Td>
                    <Td align="right">{r.active_grants}</Td>
                    <Td className="text-muted">{r.last_activity_at ? fmtRelative(r.last_activity_at, 'en') : '—'}</Td>
                    <Td className="text-muted">{fmtRelative(r.updatedAt, 'en')}</Td>
                  </Tr>
                ))}
                {rooms.data?.rooms.length === 0 ? <tr><Td colSpan={6} className="py-8 text-center text-muted">No rooms yet. Create the first one.</Td></tr> : null}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen} title="New room" description="Rooms start as drafts. Open them when the content is ready." footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={() => create.mutate()} disabled={!name.trim()} loading={create.isPending}>Create</Button></>}>
        <Field label="Name" htmlFor="room-name"><Input id="room-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Series A · 2026" onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) create.mutate(); }} /></Field>
      </Dialog>
    </>
  );
}
